import type { Attributes, ExceptionContext, Span, SpanOptions } from '../types';
import { BaseLoggerProvider } from './base';

/**
 * Simple span implementation for console logging
 */
class ConsoleSpan implements Span {
  private attributes: Attributes = {};

  setAttribute(key: string, value: any): void {
    this.attributes[key] = value;
  }

  setAttributes(attributes: Attributes): void {
    this.attributes = { ...this.attributes, ...attributes };
  }

  getAttributes(): Attributes {
    return this.attributes;
  }
}

export interface ConsoleProviderOptions {
  /**
   * Enable development mode (shows trace/debug logs)
   * @default true
   */
  isDevelopment?: boolean;

  /**
   * Enable span logging (shows [SPAN START] and [SPAN END] logs)
   * @default false
   */
  logSpans?: boolean;
}

/**
 * Console-based logger provider
 * Uses native console methods for logging
 * Useful for development and testing
 */
export class ConsoleProvider extends BaseLoggerProvider {
  private isDevelopment: boolean;
  private logSpans: boolean;

  constructor(options: ConsoleProviderOptions = {}) {
    super();
    this.isDevelopment = options.isDevelopment ?? true;
    this.logSpans = options.logSpans ?? false;
  }

  trace(message: string, attributes?: Attributes): void {
    if (this.isDevelopment) {
      console.debug(`[TRACE] ${this.formatLog(message, attributes)}`);
    }
  }

  debug(message: string, attributes?: Attributes): void {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${this.formatLog(message, attributes)}`);
    }
  }

  info(message: string, attributes?: Attributes): void {
    console.info(`[INFO] ${this.formatLog(message, attributes)}`);
  }

  warn(message: string, attributes?: Attributes): void {
    console.warn(`[WARN] ${this.formatLog(message, attributes)}`);
  }

  error(message: string, attributes?: Attributes): void {
    console.error(`[ERROR] ${this.formatLog(message, attributes)}`);
  }

  fatal(message: string, attributes?: Attributes): void {
    console.error(`[FATAL] ${this.formatLog(message, attributes)}`);
  }

  async startSpan<T>(
    name: string,
    options: SpanOptions,
    callback: (span: Span) => Promise<T>
  ): Promise<T> {
    const startTime = Date.now();
    const span = new ConsoleSpan();

    if (options.attributes) {
      span.setAttributes(options.attributes);
    }

    if (this.logSpans) {
      console.log(`[SPAN START] ${name} (${options.op})`);
    }

    try {
      const result = await callback(span);
      const duration = Date.now() - startTime;

      if (this.logSpans) {
        const attrs = span.getAttributes();
        const attrStr =
          Object.keys(attrs).length > 0 ? ` ${JSON.stringify(attrs)}` : '';
        console.log(`[SPAN END] ${name} (${duration}ms)${attrStr}`);
      }

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`[SPAN ERROR] ${name} (${duration}ms)`, error);
      throw error;
    }
  }

  captureException(error: Error, context?: ExceptionContext): void {
    const level = context?.level || 'error';
    const logMethod = level === 'fatal' ? 'error' : level;

    if (typeof console[logMethod] === 'function') {
      console[logMethod](`[EXCEPTION] ${error.message}`, {
        error,
        ...context?.context,
        tags: context?.tags,
      });
    } else {
      console.error(`[EXCEPTION] ${error.message}`, {
        error,
        ...context?.context,
        tags: context?.tags,
      });
    }
  }
}
